import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Ticket, TicketStatus, IDateRangePickerOptions, IErrorReporting, TicketType, ApplicationUserViewModel, TicketStatusId } from '../../models';
import { guidEmpty, Roles } from '../../common';
import { TicketAccessRights } from '../ticketModal/TicketAccessRights';
import { getClasses } from './getClasses';

class TicketModalDetailsController implements angular.IComponentController {
    isLoading: boolean;
    loadingMessage: string;
    accessRights: TicketAccessRights;
    formController: angular.IFormController;
    ticket: Ticket;
    categories: TicketType[] = [];
    itemCategories: TicketType[];
    currentTicketModalPage: number;
    modalId: string;
    users: ApplicationUserViewModel[];
    assignableUsers: ApplicationUserViewModel[];
    allowedStatuses: TicketStatus[];
    dueDateOptions: IDateRangePickerOptions;
    revisedDueDateOptions: IDateRangePickerOptions;
    dueRange: { date: string };
    revisedDueRange: { date: string };
    assignedUser: ApplicationUserViewModel;

    public readonly validationMessages: IErrorReporting[];
    public readonly createNewCategory: ($model) => TicketType;
    private readonly _enterToCreate: string;

    static $inject = ['$translate'];

    constructor(
        private _$translate: angular.translate.ITranslateService
    ) {
        const replace = ` ${this._$translate.instant('(Enter to create)')}`;
        this._enterToCreate = replace;
        this.createNewCategory = model => {
            // cannot use _this_ inside the method !!
            return { id: 0, name: `${model}${replace}` };
        };

        this.validationMessages = [
            {
                propertyName: 'title',
                messages: [{ key: 'required', value: 'A title is required' }]
            }, {
                propertyName: 'description',
                messages: [{ key: 'required', value: 'A description is required' }]
            }, {
                propertyName: 'dueDate',
                messages: [
                    { key: 'required', value: 'A due date is required' },
                    { key: 'overdue', value: 'Due date is exceeded' }
                ]
            }, {
                propertyName: 'assignedUser',
                messages: [{ key: 'required', value: 'An assigned user is required' }]
            }
        ];
    }

    $onInit() {
        this.assignableUsers = this.assignableUsers || this.users;
        this.updateInternalTicketProperties();
        if (this.dueDateOptions) {
            if (this.dueDateOptions.eventHandlers) {
                const eventHandler = this.dueDateOptions.eventHandlers['apply.daterangepicker'];
                this.dueDateOptions.eventHandlers['apply.daterangepicker'] = (event, data) => {
                    if (eventHandler) {
                        eventHandler(event, data);
                    }
                    this.
                        checkDueDate();
                };
            } else {
                this.dueDateOptions.eventHandlers = {
                    'apply.daterangepicker': (event, data) => {
                        const isOverdue = this.checkDueDate();
                        if (!isOverdue) {
                            this.revisedDueRange.date = this.dueRange.date;
                        }
                    }
                };
            }
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['ticket'] && changesObj['ticket'].currentValue) {
            this.updateInternalTicketProperties();
            this.assignedUser = this.getAssignedUser(this.ticket.assignedUser);
        }
        if (changesObj['users'] && this.users) {
            this.assignableUsers = this.users.filter(u => u.roles.length !== u.roles.filter(d => d === Roles.reader).length);
        }
    }

    // return class signment condition
    getClasses(name: string, isDisabled = false) {
        return getClasses(name, this.formController, isDisabled);
    }

    clearUserOnStatusChange(statusId: number) {
        if (statusId === TicketStatusId.New) {
            this.ticket.assignedUser = guidEmpty;
        }
        this.checkDueDate();
    }

    private checkDueDate() {
        const isOverDue = this.accessRights.canUpdateDueDate
            && this.ticket.statusId === TicketStatusId.Assigned
            && (this.dueRange.date && new Date(this.dueRange.date) < new Date());
        // property is valid if not overdue
        this.formController['dueDate'].$setValidity('overdue', !isOverDue);
        return isOverDue;
    }

    // Check if form has been touched
    isDirty() {
        return this.formController.$dirty;
    }

    private getAssignedUser(assignedUserId: string): ApplicationUserViewModel {
        return _.find(this.users, user => user.userId === assignedUserId);
    }

    onSelectAssignedUser(assignedUser: ApplicationUserViewModel) {
        if (assignedUser && assignedUser.userId !== guidEmpty && this.ticket.statusId === 1) {
            this.ticket.statusId = 2;
        } else if (assignedUser.userId === guidEmpty) {
            this.ticket.statusId = 1;
        }
        this.checkDueDate();
        if (assignedUser && assignedUser.userId !== guidEmpty) {
            this.ticket.assignedUser = assignedUser.userId;
        } else {
            this.ticket.assignedUser = _.find(this.users, user => user.userId === guidEmpty).userId;
        }
    }

    onSelectType(item: TicketType) {
        item.name = item.name.replace(this._enterToCreate, '');
        this.ticket.type = item;
        this.ticket.typeId = item.id;
    }

    onRemoveType(item: TicketType) {
        this.ticket.type = null;
        this.ticket.typeId = null;
    }

    onDueDateChange() {
        this.checkDueDate();
    }

    private updateInternalTicketProperties() {
        this.itemCategories = this.ticket.type ? [this.ticket.type] : [];
    }
}

export const wtTicketModalDetails: ComponentDefinition = {
    componentId: 'wtTicketModalDetails',
    controller: TicketModalDetailsController,
    template: require('./ticketModalDetails.component.html'),
    controllerAs: 'vm',
    bindings: {
        accessRights: '<',
        allowedStatuses: '<',
        categories: '<',
        currentTicketModalPage: '=',
        dueDateOptions: '=',
        revisedDueDateOptions: '=',
        dueRange: '=',
        formController: '=',
        modalId: '<',
        revisedDueRange: '=',
        ticket: '<',
        users: '<'
    }
};